import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setAuthToken, setRefreshToken, clearAuthToken } from "../graphql/client.js";
import { disconnectPresenceSocket } from "../lib/presenceSocket.js";
import { useTranslation } from "../lib/i18n.jsx";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://api-p2p-stage.palzea.com";
const DEFAULT_API_KEY = import.meta.env.VITE_DEFAULT_API_KEY || "";

const SsoCallback = () => {
  const navigate = useNavigate();
  const { setLocale } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("token");
    const apiKey = params.get("apikey") || DEFAULT_API_KEY;
    const action = params.get("action");
    const theme = params.get("theme");
    // Locale-prefixed SSO page (/{locale}/p2p-widget/sso): finish on /{locale}/p2p-widget,
    // not on "/", so the URL the user lands on is the widget page itself.
    const pathMatch = window.location.pathname.match(/^\/([^/]+)\/p2p-widget\/sso\/?$/);
    const pathLocale = pathMatch ? pathMatch[1] : null;
    const widgetBase = pathLocale ? `/${pathLocale}/p2p-widget` : "/";
    const locale = params.get("locale") || pathLocale;

    // Previously dropped locale/theme on the redirect back to "/" - only
    // apikey survived the round trip, so a widget opened with an explicit
    // locale (or theme) via this SSO hand-off silently fell back to
    // i18n.jsx's own resolution (stored preference / navigator.language)
    // once it landed home, ignoring what the caller actually asked for.
    //
    // navigate() here is a client-side route change, not a full reload, so
    // LocaleProvider's state (set once at initial mount from the URL) would
    // never pick up a locale newly present in the destination URL on its
    // own - setLocale() explicitly applies it now, in addition to putting
    // it in the URL for bookmarkability.
    if (locale) setLocale(locale);

    const goHome = () => {
      const query = new URLSearchParams({ apikey: apiKey });
      if (locale) query.set("locale", locale);
      if (theme) query.set("theme", theme);
      navigate(`${widgetBase}?${query.toString()}`, { replace: true });
    };

    // Mirror of the login SSO hand-off below, but for logout: palzea.com
    // hits this same callback with action=logout (via a hidden iframe) so
    // that logging out of the main exchange also logs out this widget,
    // wherever it's currently open (localStorage is shared across every
    // same-origin tab/frame, so clearing it here reaches all of them).
    if (action === "logout") {
      try {
        disconnectPresenceSocket();
      } catch (e) { /* best-effort */ }
      clearAuthToken();
      goHome();
      return;
    }

    if (!ssoToken) {
      goHome();
      return;
    }

    fetch(`${API_BASE}/auth/sso`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: ssoToken }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.access_token) {
          setAuthToken(data.access_token);
          if (data.refresh_token) setRefreshToken(data.refresh_token);
        }
        // Always go home — logged in if token valid, guest mode if not
        goHome();
      })
      .catch(() => {
        goHome();
      });
  }, []);

  return (
    <div className="min-h-screen h-full flex flex-col items-center justify-center">
      <div className="h-12 w-12 rounded-full border-4 border-white/20 border-t-primary animate-spin" />
    </div>
  );
};

export default SsoCallback;
